import React, { useEffect, useMemo, useRef, useState } from "react";
import { sentenceCaseLabel, useTranslation } from "../i18n";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import LoadingSpinner from "../Components/LoadingSpinner";
import CustomDropdown from "../Components/CustomDropdown";
import CreateQuimeraOrderModal from "../Components/CreateQuimeraOrderModal";
import {
  CURRENCY_OPTIONS,
  getCurrencyCode,
  QUANTITY_UNIT_OPTIONS,
} from "../data/currencyOptions";
import {
  createInquiryQuotationAction,
  getInquiryAction,
} from "../actions/inquiry";
import { backendUrl, RoutesPaths } from "../Router/constant";
import quimeraOrderService from "../services/quimeraOrder.service";
import { getVendorProfileView } from "../utils/vendorProfile";
import {
  handleNumericInputKeyDown,
  sanitizeNumericInput,
} from "../utils/numericInput";

const getInquiryValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");
const EMPTY_DISPLAY_VALUE = "\u2014";

const getInquiryText = (
  value,
  keys = [
    "name",
    "productName",
    "title",
    "label",
    "value",
    "amount",
    "email",
    "username",
  ],
) => {
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (Array.isArray(value))
    return value
      .map((item) => getInquiryText(item, keys))
      .filter(Boolean)
      .join(", ");
  if (!value || typeof value !== "object") return "";
  const text = getInquiryValue(...keys.map((key) => value[key]));
  return text === undefined ? "" : String(text);
};

const getPublicInquiryId = (...values) => {
  const candidates = values.flatMap((value) =>
    value && typeof value === "object"
      ? [
          value.inquiryId,
          value.displayInquiryId,
          value.referenceNumber,
          value.code,
          value.id,
        ]
      : [value],
  );
  const publicId = candidates
    .map((value) => getInquiryText(value).trim())
    .find((value) => value && !/^[a-f0-9]{24}$/i.test(value));
  return publicId || EMPTY_DISPLAY_VALUE;
};
const formatInquiryDate = (value, includeTime = false) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return getInquiryText(value) || "—";
  return date.toLocaleString(
    undefined,
    includeTime
      ? {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }
      : { day: "2-digit", month: "short", year: "numeric" },
  );
};

const formatInquiryLocation = (value) => {
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (!value || typeof value !== "object") return "";
  return [
    value.addressLine1,
    value.addressLine2,
    value.address,
    value.city,
    value.state,
    value.province,
    value.postalCode,
    value.countryName,
    getInquiryText(value.country, ["name", "title", "label"]),
  ]
    .filter(Boolean)
    .join(", ");
};
const getInquiryImageUrl = (image) => {
  if (image && typeof image === "object") {
    const imageId = getInquiryValue(
      image.uploadId,
      image.fileId,
      image.imageId,
      image._id,
      image.id,
    );
    if (imageId)
      return backendUrl + "/uploads/" + encodeURIComponent(String(imageId));
    image = getInquiryValue(image.url, image.secure_url, image.src, image.path);
  }
  if (typeof image !== "string" || !image.trim())
    return "/assets/inquiries-icon.svg";
  const value = image.trim();
  if (/^[a-z]:[\\/]/i.test(value) || /^\\\\/.test(value))
    return "/assets/inquiries-icon.svg";
  if (/^(https?:|data:|blob:|\/)/i.test(value)) {
    return value.startsWith("/uploads/") ? backendUrl + value : value;
  }
  return backendUrl + "/uploads/" + encodeURIComponent(value);
};

const getInquiryQuantity = (line, product, inquiry) => {
  const quantity = getInquiryValue(
    line.requestedQuantity,
    line.quantity,
    line.volume,
    product.quantity,
    inquiry.requestedQuantity,
    inquiry.quantity,
    inquiry.volume,
  );
  const amount =
    quantity && typeof quantity === "object"
      ? getInquiryValue(quantity.amount, quantity.value, quantity.quantity)
      : quantity;
  const unitValue = getInquiryValue(
    quantity && typeof quantity === "object" ? quantity.unit : undefined,
    line.quantityUnit,
    line.unit,
    product.quantityUnit,
    product.unit,
    inquiry.quantityUnit,
    inquiry.unit,
  );
  const unit = getInquiryText(unitValue);
  if (amount === undefined || amount === null || amount === "") return "—";
  return String(amount) + (unit ? " " + unit : "");
};

const getInquiryPrice = (value, unit) => {
  if (value && typeof value === "object") {
    const amount = getInquiryValue(value.amount, value.value, value.price);
    const currency = getInquiryText(
      getInquiryValue(value.currencyCode, value.currency, value.symbol),
    );
    const priceUnit = getInquiryText(
      getInquiryValue(value.unit, value.perUnit, unit),
    );
    if (amount !== undefined)
      return (
        (currency ? currency + " " : "") +
        String(amount) +
        (priceUnit ? " / " + priceUnit : "")
      );
  }
  const text = getInquiryText(value);
  if (!text) return "—";
  return text + (unit && !text.includes(String(unit)) ? " / " + unit : "");
};

const SHARE_PRICE_CURRENCY_OPTIONS = CURRENCY_OPTIONS.map((currency) => ({
  value: currency.code,
  code: currency.code,
  countryCode: currency.countryCode,
  symbol: currency.symbol,
  displayCountry: true,
  label: currency.countryName + " (" + currency.symbol + ")",
  description: currency.code,
  searchText: [
    currency.countryName,
    currency.code,
    currency.symbol,
    currency.name,
  ].join(" "),
}));

const formatSharedOfferPrice = (offer) => {
  const amount = String(offer?.quotedPrice ?? "").trim();
  if (!amount || amount === "—") return "—";
  const currencyValue = String(offer?.currency ?? "").trim();
  const amountCurrencyCode = amount.match(/^([A-Z]{3})(?=\s|\d)/i)?.[1];
  const currencyCode =
    offer?.currencyCode ||
    CURRENCY_OPTIONS.find((currency) => currency.code === currencyValue)?.code ||
    amountCurrencyCode?.toUpperCase();
  const symbol =
    offer?.currencySymbol ||
    CURRENCY_OPTIONS.find((currency) => currency.code === currencyCode)?.symbol ||
    (currencyValue && !/^[A-Z]{3}$/.test(currencyValue) ? currencyValue : "");
  const displayCurrency = symbol || currencyCode || currencyValue;
  const hasSymbolPrefix = Boolean(symbol && amount.startsWith(symbol));
  const hasCodePrefix = Boolean(
    currencyCode && amount.toUpperCase().startsWith(currencyCode.toUpperCase()),
  );
  const amountWithoutCurrency = hasSymbolPrefix
    ? amount.slice(symbol.length).trim()
    : hasCodePrefix
      ? amount.slice(currencyCode.length).trim()
      : amount;
  const normalizedAmount = amountWithoutCurrency.replace(/,/g, "");
  const isNumericAmount = /^-?\d+(?:\.\d+)?$/.test(normalizedAmount);
  const formattedAmount = isNumericAmount
    ? normalizedAmount.replace(
        /^(-?\d+)(\.\d+)?$/,
        (_, integer, fraction = "") =>
          integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + fraction,
      )
    : amount;

  if (!isNumericAmount && (hasSymbolPrefix || hasCodePrefix)) return amount;
  if (!displayCurrency) return formattedAmount;
  return displayCurrency + " " + formattedAmount;
};

const formatSharedOfferQuantity = (offer, fallback = "—") => {
  const quantity = String(offer?.quantity ?? "").trim();
  const unit = String(offer?.unit ?? "").trim();
  if (!quantity) return fallback;
  if (!unit || quantity.toLowerCase().endsWith(unit.toLowerCase())) return quantity;
  return quantity + " " + unit;
};

const formatInquiryStatus = (status) => sentenceCaseLabel(status || "Unknown");

const isQuotationRecord = (value) =>
  Boolean(
    value &&
      typeof value === "object" &&
      [
        "quotedPrice",
        "quotePrice",
        "offerPrice",
        "price",
        "quotedAmount",
        "amount",
        "totalPrice",
        "currency",
        "currencyCode",
        "quantity",
        "quotedQuantity",
        "unit",
        "leadTime",
        "deadlineForOrder",
        "preferredPaymentTerms",
        "description",
        "message",
        "notes",
        "status",
        "quotationStatus",
        "quoteStatus",
        "responseStatus",
        "statusText",
      ].some((key) => value[key] !== undefined && value[key] !== null),
  );

const getInquiryQuotationRecords = (raw) => {
  const vendorResponse = raw?.vendorResponse || {};
  const collection = [
    raw?.vendorQuotations,
    vendorResponse?.vendorQuotations,
    raw?.quotations,
    raw?.quotes,
    raw?.quotationHistory,
    raw?.vendorQuotes,
    vendorResponse?.quotations,
    vendorResponse?.quotes,
    vendorResponse?.quotationHistory,
  ].find((value) => Array.isArray(value) && value.length > 0);

  if (!collection) return [];

  const getTimestamp = (record) =>
    new Date(
      getInquiryValue(
        record.createdAt,
        record.quotedAt,
        record.respondedAt,
        record.submittedAt,
      ),
    ).getTime();
  const hasCompleteTimestamps = collection.every(
    (record) =>
      record &&
      typeof record === "object" &&
      Number.isFinite(getTimestamp(record)),
  );

  return collection
    .map((record, index) => ({ record, index }))
    .filter(({ record }) => record && typeof record === "object")
    .sort((left, right) => {
      if (hasCompleteTimestamps)
        return (
          getTimestamp(right.record) - getTimestamp(left.record) ||
          right.index - left.index
        );
      // Quotation APIs commonly append new submissions to the end of history.
      return right.index - left.index;
    })
    .map(({ record }) =>
      isQuotationRecord(record) ? record : getLatestQuotationRecord(record),
    );
};

const getLatestQuotationRecord = (value) => {
  if (Array.isArray(value)) {
    const records = value.filter(
      (item) => item && typeof item === "object" && !Array.isArray(item),
    );
    if (!records.length) return null;

    const datedRecords = records
      .map((record) => ({
        record,
        timestamp: new Date(
          getInquiryValue(
            record.createdAt,
            record.updatedAt,
            record.quotedAt,
            record.respondedAt,
          ),
        ).getTime(),
      }))
      .filter((item) => Number.isFinite(item.timestamp));

    const latestRecord = datedRecords.length
      ? datedRecords.reduce((latest, item) =>
          item.timestamp >= latest.timestamp ? item : latest,
        ).record
      : records[records.length - 1];
    return getLatestQuotationRecord(latestRecord);
  }

  if (!value || typeof value !== "object") return null;

  const nestedRecord = getInquiryValue(
    value.latestQuotation,
    value.latestQuote,
    value.quotation,
    value.quotationData,
    value.quoteData,
    value.quote,
    value.offer,
  );
  if (nestedRecord && nestedRecord !== value)
    return getLatestQuotationRecord(nestedRecord);

  if (!isQuotationRecord(value) && value.data && typeof value.data === "object")
    return getLatestQuotationRecord(value.data);

  return value;
};

const getInquiryQuotation = (raw) => {
  const vendorResponse = raw?.vendorResponse || {};
  const quotationCandidates = [
    raw?.latestQuotation,
    raw?.latestQuote,
    raw?.latestVendorQuotation,
    raw?.vendorQuotation,
    raw?.vendorQuote,
    raw?.quotationDetails,
    raw?.quotationData,
    raw?.vendorQuotationDetails,
    raw?.quotation,
    raw?.quotationHistory,
    vendorResponse?.latestQuotation,
    vendorResponse?.latestQuote,
    vendorResponse?.quotation,
    vendorResponse?.quote,
    vendorResponse?.response,
    vendorResponse?.offer,
    vendorResponse?.quotationDetails,
    vendorResponse?.quotationData,
    vendorResponse?.quotationHistory,
    raw?.vendorQuotations,
    raw?.vendorQuotes,
    raw?.vendorResponses,
    raw?.quotations,
    raw?.quotes,
    raw?.responses,
    vendorResponse?.quotations,
    vendorResponse?.quotes,
    vendorResponse,
  ];

  return (
    quotationCandidates
      .map(getLatestQuotationRecord)
      .find(isQuotationRecord) || null
  );
};

const getQuotationText = (value) =>
  getInquiryText(value, [
    "symbol",
    "currencySymbol",
    "code",
    "currencyCode",
    "name",
    "value",
    "text",
    "content",
    "body",
    "message",
    "description",
    "details",
    "terms",
    "notes",
    "unit",
    "status",
    "leadTime",
    "deadlineForOrder",
    "preferredPaymentTerms",
    "quantity",
    "amount",
    "label",
    "title",
  ]);

const getInquiryQuotationNotes = (raw, quotation) => {
  const vendorResponse = raw?.vendorResponse || {};
  const sources = [
    quotation,
    raw?.sharedOffer,
    raw?.sharedOffers,
    raw?.sharedOfferDetails,
    raw?.offerDetails,
    raw?.latestQuotation,
    raw?.latestQuote,
    raw?.latestVendorQuotation,
    raw?.vendorQuotation,
    raw?.vendorQuote,
    raw?.quotationDetails,
    raw?.quotationData,
    raw?.vendorQuotationDetails,
    raw?.quotation,
    raw?.quotationHistory,
    raw?.vendorQuotations,
    raw?.vendorQuotes,
    raw?.vendorResponses,
    raw?.quotations,
    raw?.quotes,
    raw?.responses,
    vendorResponse?.latestQuotation,
    vendorResponse?.latestQuote,
    vendorResponse?.quotation,
    vendorResponse?.quote,
    vendorResponse?.response,
    vendorResponse?.offer,
    vendorResponse?.quotationDetails,
    vendorResponse?.quotationData,
    vendorResponse?.quotationHistory,
    vendorResponse?.quotations,
    vendorResponse?.quotes,
    vendorResponse?.sharedOffer,
    vendorResponse?.sharedOfferDetails,
    vendorResponse?.offerDetails,
    vendorResponse?.offers,
    vendorResponse?.responseData,
    vendorResponse,
  ];

  return (
    sources
      .flatMap((source) => [source, getLatestQuotationRecord(source)])
      .map((source) =>
        getInquiryValue(
          source?.notes,
          source?.offerNotes,
          source?.note,
          source?.terms,
          source?.offerTerms,
        ),
      )
      .map(getQuotationText)
      .find(Boolean) || ""
  );
};

const mapInquiryQuotationRecord = (quotation) => {
  const price = getInquiryValue(
    quotation?.quotedPrice,
    quotation?.quotePrice,
    quotation?.offerPrice,
    quotation?.price,
    quotation?.quotedAmount,
    quotation?.amount,
    quotation?.totalPrice,
  );
  const priceAmount =
    price && typeof price === "object"
      ? getInquiryValue(price.amount, price.value, price.price)
      : price;
  const currencyValue = getInquiryValue(
    quotation?.currencySymbol,
    quotation?.currency,
    quotation?.currencyCode,
    price && typeof price === "object" ? price.currencySymbol : undefined,
    price && typeof price === "object" ? price.currencyCode : undefined,
    price && typeof price === "object" ? price.currency : undefined,
    price && typeof price === "object" ? price.symbol : undefined,
  );
  const currencyText = getQuotationText(currencyValue);
  const matchingCurrency =
    CURRENCY_OPTIONS.find((currency) => currency.code === currencyText) ||
    CURRENCY_OPTIONS.find(
      (currency) =>
        currency.name.toLowerCase() === currencyText.toLowerCase(),
    ) ||
    CURRENCY_OPTIONS.find(
      (currency) =>
        currency.symbol === currencyText &&
        (currencyText !== "$" || currency.code === "USD"),
    );
  const currencyCode =
    matchingCurrency?.code ||
    (currencyText ? getCurrencyCode(currencyText) : "");
  const quantityValue = getInquiryValue(
    quotation?.quantity,
    quotation?.quotedQuantity,
    quotation?.volume,
  );
  const quantity =
    quantityValue && typeof quantityValue === "object"
      ? getInquiryValue(
          quantityValue.amount,
          quantityValue.quantity,
          quantityValue.value,
        )
      : quantityValue;
  const submittedAtValue = getInquiryValue(
    quotation?.createdAt,
    quotation?.quotedAt,
    quotation?.respondedAt,
    quotation?.submittedAt,
  );
  const description = getQuotationText(
    getInquiryValue(quotation?.description, quotation?.details, quotation?.reason),
  );
  const message = getQuotationText(
    getInquiryValue(
      quotation?.message,
      quotation?.messageText,
      quotation?.responseMessage,
      quotation?.comment,
      description,
    ),
  );
  const additionalFields = [
    {
      label: "Payment terms",
      value: getQuotationText(
        getInquiryValue(quotation?.paymentTerms, quotation?.paymentTerm),
      ),
    },
    {
      label: "Delivery terms",
      value: getQuotationText(
        getInquiryValue(
          quotation?.deliveryTerms,
          quotation?.deliveryTerm,
          quotation?.incoterms,
        ),
      ),
    },
    {
      label: "Delivery location",
      value: formatInquiryLocation(
        getInquiryValue(quotation?.deliveryLocation, quotation?.shippingAddress),
      ),
    },
    {
      label: "Valid until",
      value: getInquiryValue(
        quotation?.validUntil,
        quotation?.expiresAt,
        quotation?.expiryDate,
      )
        ? formatInquiryDate(
            getInquiryValue(
              quotation?.validUntil,
              quotation?.expiresAt,
              quotation?.expiryDate,
            ),
          )
        : "",
    },
  ].filter((field) => field.value);

  return {
    vendorName: getInquiryText(
      getInquiryValue(
        quotation?.vendorName,
        quotation?.vendor?.companyName,
        quotation?.vendor?.name,
        quotation?.vendor?.username,
        quotation?.supplierName,
      ),
    ),
    status: formatInquiryStatus(
      getQuotationText(
        getInquiryValue(
          quotation?.status,
          quotation?.quotationStatus,
          quotation?.quoteStatus,
          quotation?.responseStatus,
          quotation?.statusText,
        ),
      ) || "Pending",
    ),
    quotedPrice: getInquiryText(priceAmount),
    currency: currencyText,
    currencyCode,
    currencySymbol:
      getInquiryText(quotation?.currencySymbol) ||
      matchingCurrency?.symbol ||
      (currencyText && currencyText !== currencyCode ? currencyText : ""),
    quantity: getInquiryText(quantity),
    unit: getQuotationText(
      getInquiryValue(
        quotation?.unit,
        quotation?.quantityUnit,
        quotation?.priceUnit,
        quantityValue && typeof quantityValue === "object"
          ? quantityValue.unit
          : undefined,
      ),
    ),
    leadTime: getQuotationText(
      getInquiryValue(
        quotation?.leadTime,
        quotation?.deliveryTime,
        quotation?.shippingTime,
      ),
    ),
    message,
    notes: getQuotationText(
      getInquiryValue(
        quotation?.notes,
        quotation?.offerNotes,
        quotation?.note,
        quotation?.terms,
        quotation?.offerTerms,
      ),
    ),
    quotationId: getInquiryText(quotation?.quotationId, [
      "_id",
      "id",
      "quotationId",
      "value",
    ]),
    reference: getInquiryText(
      getInquiryValue(
        quotation?.quotationId,
        quotation?.referenceNumber,
        quotation?.quoteNumber,
        quotation?._id,
        quotation?.id,
      ),
      ["_id", "id", "quotationId", "value"],
    ),
    submittedAt: submittedAtValue
      ? formatInquiryDate(submittedAtValue)
      : "",
    additionalFields,
  };
};
const mapInquiryDetail = (raw, routeId) => {
  if (!raw || typeof raw !== "object") return null;
  const requirements = Array.isArray(raw.requirements) ? raw.requirements : [];
  let entries = requirements.flatMap((line) => {
    const products = Array.isArray(line.productIds)
      ? line.productIds
      : getInquiryValue(line.product, line.productId)
        ? [getInquiryValue(line.product, line.productId)]
        : [];
    return products.map((product) => ({ line, product }));
  });
  if (!entries.length && Array.isArray(raw.products))
    entries = raw.products.map((product) => ({ line: product, product }));
  if (!entries.length)
    entries = [
      {
        line: raw,
        product:
          getInquiryValue(raw.product, raw.productId, raw.commodity) || raw,
      },
    ];

  const productDetails = entries.map(({ line, product: productValue }) => {
    const product =
      productValue && typeof productValue === "object"
        ? productValue.product || productValue.productId || productValue
        : {};
    const name =
      getInquiryText(product, ["name", "productName", "title", "label"]) ||
      getInquiryText(productValue, ["name", "productName", "title", "label"]) ||
      getInquiryText(
        getInquiryValue(
          line.productName,
          line.productTitle,
          raw.productName,
          raw.productTitle,
          raw.commodity,
        ),
      );
    const sector = getInquiryValue(
      line.sectorId,
      line.sector,
      product.sector,
      raw.sector,
    );
    return {
      name,
      sector:
        getInquiryText(sector, ["name", "title", "label"]) ||
        getInquiryText(
          getInquiryValue(line.sectorName, product.sectorName, raw.sectorName),
        ),
      quantity: getInquiryQuantity(line, product, raw),
      image: getInquiryImageUrl(
        getInquiryValue(
          product.mainImage,
          product.image,
          product.thumbnail,
          line.mainImage,
          line.image,
          raw.mainImage,
          raw.productImage,
        ),
      ),
      grade: getInquiryText(
        getInquiryValue(
          line.grade,
          line.quality,
          product.grade,
          product.quality,
          product.specification,
          raw.grade,
          raw.quality,
        ),
      ),
      targetPrice: getInquiryPrice(
        getInquiryValue(
          line.targetPrice,
          line.price,
          raw.targetPrice,
          raw.price,
        ),
        getInquiryText(getInquiryValue(line.priceUnit, raw.priceUnit)),
      ),
      tolerance: getInquiryText(
        getInquiryValue(
          line.tolerance,
          line.quantityTolerance,
          raw.tolerance,
          raw.quantityTolerance,
        ),
      ),
    };
  });

  const firstLine = entries[0] ? entries[0].line : requirements[0] || raw;
  const firstProduct = productDetails[0] || {};
  const collectProductField = (field) =>
    Array.from(
      new Set(
        productDetails
          .map((product) => product[field])
          .filter((value) => value && value !== "—"),
      ),
    ).join(", ");
  const client =
    getInquiryValue(
      raw.client,
      raw.clientId,
      raw.buyer,
      raw.customer,
      raw.user,
      raw.createdBy,
    ) || {};
  const clientObject = typeof client === "object" ? client : {};
  const vendorResponse = raw.vendorResponse || {};
  const quotation = getInquiryQuotation(raw);
  const quotationRecords = getInquiryQuotationRecords(raw);
  const status =
    getInquiryText(
      getInquiryValue(vendorResponse.status, raw.status, raw.inquiryStatus),
    ) || "Unknown";
  const statusKey = status
    .toLowerCase()
    .trim()
    .replace(/[\s-]+/g, "_");
  const statusClass =
    {
      new: "new",
      pending: "pending",
      quoted: "quoted",
      under_review: "under-review",
      negotiation: "negotiation",
      converted: "converted",
      closed: "closed",
      accepted: "accepted",
      completed: "completed",
      cancelled: "cancelled",
      rejected: "cancelled",
      reject: "cancelled",
      processing: "processing",
    }[statusKey] || "new";
  const country = getInquiryText(
    getInquiryValue(
      clientObject.country,
      clientObject.countryName,
      clientObject.address && clientObject.address.country,
      raw.country,
      raw.countryName,
      raw.deliveryCountry,
    ),
  );
  const countryFlag = getInquiryText(
    getInquiryValue(
      clientObject.countryFlag,
      clientObject.flag,
      raw.countryFlag,
      raw.flag,
    ),
  );
  const delivery = getInquiryValue(
    raw.deliveryLocation,
    raw.destination,
    raw.deliveryAddress,
    raw.shippingAddress,
  );
  const sourceTimeline = getInquiryValue(
    raw.timeline,
    raw.activities,
    raw.activity,
    raw.history,
  );
  const timeline = Array.isArray(sourceTimeline)
    ? sourceTimeline.map((item) => ({
        title:
          getInquiryText(
            getInquiryValue(item.title, item.event, item.action, item.status),
          ) || "Inquiry update",
        description: getInquiryText(
          getInquiryValue(
            item.description,
            item.message,
            item.details,
            item.reason,
          ),
        ),
        time: formatInquiryDate(
          getInquiryValue(item.createdAt, item.updatedAt, item.date, item.time),
          true,
        ),
        status: String(
          item.status || (item.completed ? "completed" : "current"),
        ).toLowerCase(),
      }))
    : [];
  const shipmentSource = getInquiryValue(
    raw.shipment,
    raw.shippingDetails,
    raw.tracking,
  );
  const shipmentStages =
    shipmentSource &&
    getInquiryValue(shipmentSource.stages, shipmentSource.timeline);
  const shipment =
    Array.isArray(shipmentStages) && shipmentStages.length
      ? {
          ...shipmentSource,
          stages: shipmentStages.map((stage) => ({
            title:
              getInquiryText(
                getInquiryValue(stage.title, stage.name, stage.status),
              ) || "—",
            date: formatInquiryDate(
              getInquiryValue(stage.date, stage.updatedAt, stage.createdAt),
            ),
            completed: Boolean(stage.completed || stage.isCompleted),
            current: Boolean(stage.current || stage.isCurrent),
            description: getInquiryText(
              getInquiryValue(stage.description, stage.details, stage.message),
            ),
          })),
        }
      : null;

  const offerPrice = getInquiryValue(
    quotation?.quotedPrice,
    quotation?.quotePrice,
    quotation?.offerPrice,
    quotation?.price,
    quotation?.quotedAmount,
    quotation?.amount,
    quotation?.totalPrice,
  );
  const offerPriceAmount =
    offerPrice && typeof offerPrice === "object"
      ? getInquiryValue(offerPrice.amount, offerPrice.value, offerPrice.price)
      : offerPrice;
  const offerDescription = getQuotationText(
    getInquiryValue(
      quotation?.description,
      quotation?.details,
      quotation?.reason,
    ),
  );
  const offerMessage = getQuotationText(
    getInquiryValue(
      quotation?.message,
      quotation?.messageText,
      quotation?.responseMessage,
      quotation?.comment,
    ),
  );
  const offerQuantityValue = getInquiryValue(
    quotation?.quantity,
    quotation?.quotedQuantity,
    quotation?.volume,
  );
  const offerQuantity =
    offerQuantityValue && typeof offerQuantityValue === "object"
      ? getInquiryValue(
          offerQuantityValue.amount,
          offerQuantityValue.quantity,
          offerQuantityValue.value,
        )
      : offerQuantityValue;
  const offerCurrencyValue = getInquiryValue(
    quotation?.currencySymbol,
    quotation?.currency,
    quotation?.currencyCode,
    offerPrice && typeof offerPrice === "object"
      ? offerPrice.currencySymbol
      : undefined,
    offerPrice && typeof offerPrice === "object"
      ? offerPrice.currencyCode
      : undefined,
    offerPrice && typeof offerPrice === "object"
      ? offerPrice.currency
      : undefined,
    offerPrice && typeof offerPrice === "object"
      ? offerPrice.symbol
      : undefined,
  );
  const offerCurrencyText = getQuotationText(offerCurrencyValue);
  const matchingOfferCurrency =
    CURRENCY_OPTIONS.find((currency) => currency.code === offerCurrencyText) ||
    CURRENCY_OPTIONS.find(
      (currency) =>
        currency.name.toLowerCase() === offerCurrencyText.toLowerCase(),
    ) ||
    CURRENCY_OPTIONS.find(
      (currency) =>
        currency.symbol === offerCurrencyText &&
        (offerCurrencyText !== "$" || currency.code === "USD"),
    );
  const offerCurrencyCode =
    matchingOfferCurrency?.code ||
    (offerCurrencyText ? getCurrencyCode(offerCurrencyText) : "");
  const offerCurrencySymbol =
    getInquiryText(quotation?.currencySymbol) ||
    matchingOfferCurrency?.symbol ||
    (offerCurrencyText && offerCurrencyText !== offerCurrencyCode
      ? offerCurrencyText
      : "");
  const offerStatus = getQuotationText(
    getInquiryValue(
      quotation?.status,
      quotation?.quotationStatus,
      quotation?.quoteStatus,
      quotation?.responseStatus,
      quotation?.statusText,
      vendorResponse.status,
    ),
  );
  const offerUnit = getQuotationText(
    getInquiryValue(
      quotation?.unit,
      quotation?.quantityUnit,
      quotation?.priceUnit,
      offerQuantityValue && typeof offerQuantityValue === "object"
        ? offerQuantityValue.unit
        : undefined,
    ),
  );
  const offerLeadTime = getQuotationText(
    getInquiryValue(
      quotation?.leadTime,
      quotation?.deliveryTime,
      quotation?.shippingTime,
    ),
  );
  const offerDeadlineForOrder = getQuotationText(
    getInquiryValue(
      quotation?.deadlineForOrder,
      vendorResponse?.deadlineForOrder,
      raw?.deadlineForOrder,
      quotation?.orderDeadline,
    ),
  );
  const offerPreferredPaymentTerms = getQuotationText(
    getInquiryValue(
      quotation?.preferredPaymentTerms,
      vendorResponse?.preferredPaymentTerms,
      raw?.preferredPaymentTerms,
      quotation?.preferredPaymentTerm,
    ),
  );
  const offerNotes = getInquiryQuotationNotes(raw, quotation);
  const offerAdditionalFields = [
    {
      label: "Payment terms",
      value: getQuotationText(
        getInquiryValue(quotation?.paymentTerms, quotation?.paymentTerm),
      ),
    },
    {
      label: "Delivery terms",
      value: getQuotationText(
        getInquiryValue(
          quotation?.deliveryTerms,
          quotation?.deliveryTerm,
          quotation?.incoterms,
        ),
      ),
    },
    {
      label: "Delivery location",
      value: formatInquiryLocation(
        getInquiryValue(
          quotation?.deliveryLocation,
          quotation?.shippingAddress,
        ),
      ),
    },
    {
      label: "Valid until",
      value: getInquiryValue(
        quotation?.validUntil,
        quotation?.expiresAt,
        quotation?.expiryDate,
      )
        ? formatInquiryDate(
            getInquiryValue(
              quotation?.validUntil,
              quotation?.expiresAt,
              quotation?.expiryDate,
            ),
          )
        : "",
    },
  ].filter((field) => field.value);
  const hasOffer =
    offerPrice !== undefined ||
    Boolean(offerDescription) ||
    Boolean(offerMessage) ||
    Boolean(offerLeadTime) ||
    Boolean(offerDeadlineForOrder) ||
    Boolean(offerPreferredPaymentTerms) ||
    Boolean(offerUnit) ||
    offerQuantity !== undefined ||
    Boolean(offerNotes) ||
    Boolean(offerCurrencyText) ||
    offerAdditionalFields.length > 0;
  const currentSharedOffer = hasOffer
    ? {
        status: formatInquiryStatus(offerStatus || "Pending"),
        quotedPrice: getInquiryText(offerPriceAmount),
        currency: offerCurrencyText,
        currencyCode: offerCurrencyCode,
        currencySymbol: offerCurrencySymbol,
        quantity: getInquiryText(offerQuantity),
        unit: offerUnit,
        leadTime: offerLeadTime,
        deadlineForOrder: offerDeadlineForOrder,
        preferredPaymentTerms: offerPreferredPaymentTerms,
        description: offerDescription,
        message: offerMessage,
        notes: offerNotes,
        quotationId: getInquiryText(quotation?.quotationId, [
          "_id",
          "id",
          "quotationId",
          "value",
        ]),
        reference: getInquiryText(
          getInquiryValue(
            quotation?.quotationId,
            quotation?.referenceNumber,
            quotation?.quoteNumber,
            quotation?._id,
            quotation?.id,
          ),
          ["_id", "id", "quotationId", "value"],
        ),
        submittedAt: getInquiryValue(
          quotation?.createdAt,
          quotation?.quotedAt,
          quotation?.respondedAt,
        )
          ? formatInquiryDate(
              getInquiryValue(
                quotation?.createdAt,
                quotation?.quotedAt,
                quotation?.respondedAt,
              ),
            )
          : "",
        additionalFields: offerAdditionalFields,
      }
    : null;
  const sharedOffers = quotationRecords.length
    ? quotationRecords.map(mapInquiryQuotationRecord)
    : currentSharedOffer
      ? [currentSharedOffer]
      : [];
  const clientName =
    getInquiryText(client, ["companyName", "name", "username", "email"]) ||
    getInquiryText(
      getInquiryValue(
        raw.clientName,
        raw.buyerName,
        raw.customerName,
        raw.companyName,
        raw.seller,
      ),
    );
  const inquiryDate = formatInquiryDate(
    getInquiryValue(raw.createdAt, raw.submittedAt, raw.inquiryDate, raw.date),
  );
  const phoneValue = getInquiryText(
    getInquiryValue(
      clientObject.phoneNumber,
      clientObject.phone,
      raw.clientPhone,
      raw.buyerPhone,
      raw.phoneNumber,
      raw.phone,
    ),
  );
  const phoneCountryCode = getInquiryText(
    getInquiryValue(
      clientObject.countryCode,
      clientObject.phoneCountryCode,
      raw.countryCode,
      raw.phoneCountryCode,
    ),
  );
  const clientPhone =
    phoneValue && phoneCountryCode && !phoneValue.startsWith(phoneCountryCode)
      ? phoneCountryCode + " " + phoneValue
      : phoneValue;

  return {
    id: getPublicInquiryId(
      raw.displayInquiryId,
      raw.inquiryId,
      raw.referenceNumber,
      raw.code,
      raw.id,
    ),
    title:
      productDetails
        .map((item) => item.name)
        .filter(Boolean)
        .join(", ") ||
      getInquiryText(getInquiryValue(raw.title, raw.subject, raw.commodity)) ||
      "—",
    image: firstProduct.image || "/assets/inquiries-icon.svg",
    seller: clientName || "—",
    country: country || "—",
    flag: countryFlag,
    commodity:
      productDetails
        .map((item) => item.name)
        .filter(Boolean)
        .join(", ") ||
      getInquiryText(
        getInquiryValue(
          raw.productName,
          raw.productTitle,
          raw.commodity,
          raw.title,
        ),
      ) ||
      "—",
    sector: collectProductField("sector") || "—",
    grade: collectProductField("grade") || "—",
    quantity:
      productDetails
        .map((item) => item.quantity)
        .filter((value) => value !== "—")
        .join(", ") || getInquiryQuantity(firstLine, {}, raw),
    tolerance: collectProductField("tolerance") || "—",
    status,
    statusClass,
    statusText:
      getInquiryText(
        getInquiryValue(vendorResponse.statusText, raw.statusText),
      ) || formatInquiryStatus(status),
    lastUpdate: formatInquiryDate(
      getInquiryValue(
        raw.updatedAt,
        raw.modifiedAt,
        vendorResponse.respondedAt,
      ),
    ),
    date: inquiryDate,
    inquiryDate,
    deliveryLocation: formatInquiryLocation(delivery) || "—",
    destinationPortLocation:
      formatInquiryLocation(raw.destinationPortLocation) ||
      getInquiryText(raw.destinationPortLocation) ||
      "—",
    deliveryTerm:
      getInquiryText(
        getInquiryValue(
          raw.deliveryTerm,
          raw.deliveryTerms,
          raw.incoterm,
          raw.incoterms,
        ),
      ) || "—",
    paymentTerm:
      getInquiryText(
        getInquiryValue(raw.paymentTerm, raw.paymentTerms, raw.paymentMethod),
      ) || "—",
    targetPrice:
      collectProductField("targetPrice") ||
      getInquiryPrice(
        getInquiryValue(raw.targetPrice, raw.price),
        raw.priceUnit,
      ),
    buyerReference:
      getInquiryText(
        getInquiryValue(
          raw.buyerReference,
          raw.referenceNumber,
          raw.clientReference,
          raw.rfqNumber,
          raw.code,
        ),
      ) || "—",
    preferredShipment:
      getInquiryText(
        getInquiryValue(
          raw.preferredShipment,
          raw.shipmentMethod,
          raw.shippingMethod,
          raw.transportMode,
        ),
      ) || "—",
    portOfLoading:
      getInquiryText(
        getInquiryValue(raw.portOfLoading, raw.loadingPort, raw.originPort),
      ) || "—",
    portOfDischarge:
      getInquiryText(
        getInquiryValue(
          raw.portOfDischarge,
          raw.dischargePort,
          raw.destinationPort,
        ),
      ) || "—",
    message:
      getInquiryText(
        getInquiryValue(
          raw.message,
          raw.description,
          raw.details,
          firstLine.description,
          firstLine.requirements,
          raw.requirementsText,
          raw.inquiryMessage,
          raw.requirementsDescription,
        ),
      ) || "—",
    sellerEmail:
      getInquiryText(
        getInquiryValue(
          clientObject.email,
          clientObject.username,
          raw.clientEmail,
          raw.buyerEmail,
          raw.email,
        ),
      ) || "—",
    sellerPhone: clientPhone || "—",
    clientVerified: Boolean(
      clientObject.verified || clientObject.isVerified || raw.clientVerified,
    ),
    shipment,
    timeline,
    sharedOffer: sharedOffers[0] || currentSharedOffer,
    sharedOffers,
  };
};

export default function InquiriesDetails() {
  const { t, text, status: translateStatus } = useTranslation();
  const authMe = useSelector((state) => state.auth?.authMe || {});
  const vendorProfileView = getVendorProfileView(authMe);
  const offerVendorName =
    vendorProfileView.name ||
    vendorProfileView.email?.split("@")[0] ||
    t("ui.vendor");
  const offerVendorInitial =
    offerVendorName.trim().charAt(0).toUpperCase() || "V";
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const dropdownRef = useRef(null);
  const {
    detail: inquiryDetail,
    detailLoading,
    detailError,
    quotationLoading,
    quotationError,
  } = useSelector((state) => state.inquiry || {});

  useEffect(() => {
    if (id) dispatch(getInquiryAction(id));
  }, [dispatch, id]);

  const inquiry = useMemo(() => {
    const detailId = getInquiryText(
      getInquiryValue(
        inquiryDetail && inquiryDetail._id,
        inquiryDetail && inquiryDetail.id,
        inquiryDetail && inquiryDetail.inquiryId,
      ),
    );
    if (detailId && id && detailId !== String(id)) return null;
    return mapInquiryDetail(inquiryDetail, id);
  }, [inquiryDetail, id]);

  useEffect(() => {
    if (location.hash !== "#quotation" || !inquiry) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      document
        .getElementById("quotation")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [inquiry, location.hash]);

  const [shipmentDropdownOpen, setShipmentDropdownOpen] = useState(false);
  const [sharePriceOpen, setSharePriceOpen] = useState(false);
  const [createQuimeraOrderOpen, setCreateQuimeraOrderOpen] = useState(false);
  const [sharedOffers, setSharedOffers] = useState([]);
  const [currentOfferDetailsOpen, setCurrentOfferDetailsOpen] = useState(false);
  const [historyOfferDetailsOpen, setHistoryOfferDetailsOpen] = useState({});
  const sharedOffer = sharedOffers[0] || null;
  const currentOfferVendorName =
    sharedOffer?.vendorName || offerVendorName;
  const currentOfferVendorInitial =
    currentOfferVendorName.trim().charAt(0).toUpperCase() || "V";
  const [priceForm, setPriceForm] = useState({
    currencyCode: "INR",
    quotedPrice: "",
    quantity: "",
    unit: "",
    leadTime: "",
    description: "",
    notes: "",
  });

  const updatePriceForm = (field, value) => {
    setPriceForm((current) => ({ ...current, [field]: value }));
  };

  const canSubmitPrice = Boolean(
    CURRENCY_OPTIONS.some((currency) => currency.code === priceForm.currencyCode) &&
    priceForm.quotedPrice !== "" &&
    Number.isFinite(Number(priceForm.quotedPrice)) &&
    Number(priceForm.quotedPrice) >= 0 &&
    priceForm.quantity !== "" &&
    Number.isFinite(Number(priceForm.quantity)) &&
    Number(priceForm.quantity) >= 0
  );

  const priceCurrencySymbol = useMemo(
    () =>
      CURRENCY_OPTIONS.find(
        (currency) => currency.code === priceForm.currencyCode,
      )?.symbol || "$",
    [priceForm.currencyCode],
  );

  const submitPrice = async (event) => {
    event.preventDefault();
    const payload = {
      price: Number(priceForm.quotedPrice),
      currency: priceCurrencySymbol,
      quantity: Number(priceForm.quantity),
      unit: priceForm.unit,
      leadTime: priceForm.leadTime,
      description: priceForm.description,
      notes: priceForm.notes,
    };
    const result = await dispatch(createInquiryQuotationAction(id, payload));

    if (!result?.success) return;

    setSharePriceOpen(false);
    dispatch(getInquiryAction(id, { preserveData: true }));
  };

  const handleCreateQuimeraOrder = async (orderForm) => {
    const token = quimeraOrderService.getSavedQuimeraToken();
    if (!token) {
      throw new Error("Connect your Quimera account before creating an order.");
    }

    const payload = {
      inquiryId: String(id),
      logisticsType: orderForm.logisticsType,
      pickupAddress: orderForm.pickupAddress.trim(),
      pickupAddressDetails: orderForm.pickupAddressDetails.trim(),
      dropAddress: orderForm.dropAddress.trim(),
      dropAddressDetails: orderForm.dropAddressDetails.trim(),
      countryCode: orderForm.countryCode,
      countryName: orderForm.countryName,
      phoneCountryCode: orderForm.phoneCountryCode,
      phoneDialCode: orderForm.phoneDialCode,
      phoneNumber: orderForm.fullPhoneNumber,
      contactName: orderForm.contactName.trim(),
      pickupDate: orderForm.pickupDate,
      email: orderForm.email.trim(),
      itemName: orderForm.itemName.trim(),
      quantity: Number(orderForm.quantity),
      weightKg: Number(orderForm.weightKg),
      heightCm: orderForm.heightCm === "" ? undefined : Number(orderForm.heightCm),
      widthCm: orderForm.widthCm === "" ? undefined : Number(orderForm.widthCm),
      detailedRequirements: orderForm.detailedRequirements.trim(),
      productImage: orderForm.productImage || "",
    };

    try {
      const response = await quimeraOrderService.createQuimeraOrder(payload, token);
      const responseData = response?.data?.data ?? response?.data ?? {};
      if (
        response?.data?.status === false ||
        response?.data?.success === false ||
        responseData?.status === false ||
        responseData?.success === false
      ) {
        throw new Error(
          responseData?.message ||
            response?.data?.message ||
            "Unable to create the Quimera order.",
        );
      }
      return {
        message:
          responseData?.message ||
          response?.data?.message ||
          "Quimera order created successfully.",
      };
    } catch (error) {
      const message =
        error?.response?.data?.data?.message ||
        error?.response?.data?.message ||
        error?.message ||
        "Unable to create the Quimera order. Please try again.";
      throw new Error(message);
    }
  };

  const openInquiryChat = () => {
    navigate(`${RoutesPaths.MESSAGE}?inquiryId=${encodeURIComponent(id)}`);
  };

  const [shipment, setShipment] = useState(null);
  useEffect(() => {
    setShipment(inquiry ? inquiry.shipment : null);
    setSharedOffers(inquiry ? inquiry.sharedOffers : []);
    setShipmentDropdownOpen(false);
  }, [inquiry]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShipmentDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);
  const handleShipmentStageUpdate = (stageIndex) => {
    const updatedStages = shipment.stages.map((stage, index) => ({
      ...stage,

      completed: index <= stageIndex,

      current: index === stageIndex,

      date:
        index === stageIndex && stage.date === "Pending"
          ? "Updated now"
          : stage.date,
    }));

    const progress = Math.round(
      ((stageIndex + 1) / shipment.stages.length) * 100,
    );

    setShipment({
      ...shipment,

      status: shipment.stages[stageIndex].title,

      progress,

      stages: updatedStages,
    });

    setShipmentDropdownOpen(false);
  };

  if (!inquiry && (detailLoading || !detailError)) {
    return (
      <div className="dashboard-page inquiry-details-page">
        <button
          type="button"
          className="inquiry-details-back"
          onClick={() => navigate("/inquiries")}
        >
          <span>{"\u2190"}</span>{t("ui.back.to.inquiries")}</button>
        <LoadingSpinner
          className="page-loading-state"
          label={t("ui.loadingInquiryDetails")}
        />
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="dashboard-page inquiry-details-page">
        <button
          type="button"
          className="inquiry-details-back"
          onClick={() => navigate("/inquiries")}
        >
          <span>{"\u2190"}</span>{t("ui.back.to.inquiries")}</button>
        <div className="inquiries-empty-state" role="alert">
          <strong>{text(detailError || "Inquiry details were not found.")}</strong>
        </div>
      </div>
    );
  }
  return (
    <div className="dashboard-page inquiry-details-page">
      <button
        type="button"
        className="inquiry-details-back"
        onClick={() => navigate("/inquiries")}
      >
        <span>←</span>{t("ui.back.to.inquiries")}</button>
      <div className="inquiry-reference-view">
        <section className="reference-inquiry-header">
          <div className="reference-breadcrumb">{t("ui.request.quotes")}{" "}<span>/</span> <b>{inquiry.id}</b>
          </div>
          <div className="reference-title-row">
            <div>
              <h1>{t("ui.view.inquiry")}</h1>
              <p>{t("ui.review.the.full.quote.request.submitted.by")}{" "}
                <strong>{inquiry.seller}</strong>
              </p>
            </div>
            <div className="reference-actions">
              <button
                type="button"
                className="reference-message-btn"
                onClick={openInquiryChat}
              >
                <svg
                  className="inquiry-button-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v6a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3v-3.18A2.5 2.5 0 0 1 5 12.5v-6Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />
                </svg>{t("ui.message")}</button>
              <button
                type="button"
                className="reference-share-btn"
                onClick={() => setSharePriceOpen(true)}
              >
                <svg
                  className="inquiry-button-icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    d="m13.2 4.8 6 6-8.4 8.4H4.8v-6l8.4-8.4Z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinejoin="round"
                  />
                  <circle cx="9" cy="14" r="1.2" fill="currentColor" />
                </svg>{t("ui.share.a.price")}</button>
              <button
                type="button"
                className="reference-create-quimera-btn"
                onClick={() => {
                  setSharePriceOpen(false);
                  setCreateQuimeraOrderOpen(true);
                }}
              >
                <svg className="inquiry-button-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="m21 3-7.2 18-3.1-7.7L3 10.2 21 3Z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round" />
                  <path d="m10.7 13.3 5-5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
                Create Quimera Order
              </button>
            </div>
          </div>
          <div className="reference-summary-grid">
            <div>
              <span>{t("ui.request.code")}</span>
              <strong>{inquiry.id}</strong>
            </div>
            <div>
              <span>{t("ui.product.details")}</span>
              <strong>{inquiry.commodity}</strong>
              {/* <small>{inquiry.sector}</small> */}
            </div>
            <div>
              <span>{t("ui.submitted")}</span>
              <strong>{inquiry.inquiryDate}</strong>
            </div>
            <div>
              <span className="mb-1">{t("ui.status")}</span>
              <em className={`reference-status reference-offer-vendor-status accepted ${inquiry.statusClass}`}>
                {translateStatus(inquiry.status)}
              </em>
            </div>
          </div>
        </section>
        {(sharedOffers.length > 0 || location.hash === "#quotation") && (
          <section
            className="reference-card shared-offers-reference"
            id="quotation"
          >
            <div className="shared-offers-reference-heading">
              <h2>{t("ui.shared.price")}</h2>
              <p>{t("ui.shared.price.description")}</p>
            </div>
            {sharedOffer ? (
              <>
                <article className="reference-current-offer-card">
                  <div className="reference-current-offer-heading">
                    <div className="reference-offer-vendor">
                      <span className="reference-offer-vendor-avatar">
                        {currentOfferVendorInitial}
                      </span>
                      <div className="reference-current-offer-identity">
                        <strong>{currentOfferVendorName}</strong>
                        <small>
                          {sharedOffer.quotationId ||
                            sharedOffer.reference ||
                            EMPTY_DISPLAY_VALUE}
                        </small>
                      </div>
                    </div>
                    <em
                      className={`reference-status reference-offer-vendor-status ${sharedOffer.status.toLowerCase()}`}
                    >
                      {translateStatus(sharedOffer.status)}
                    </em>
                    <button
                      type="button"
                      className="reference-offer-toggle"
                      aria-expanded={currentOfferDetailsOpen}
                      aria-controls="reference-current-offer-details"
                      aria-label={t("ui.viewDetails")}
                      onClick={() =>
                        setCurrentOfferDetailsOpen((isOpen) => !isOpen)
                      }
                    >
                      <span>{t("ui.viewDetails")}</span>
                      <svg
                        className="reference-offer-chevron"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="m6 9 6 6 6-6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div
                    id="reference-current-offer-details"
                    className="reference-current-offer-details"
                    hidden={!currentOfferDetailsOpen}
                  >
                    <div className="reference-offer-grid">
                      <div>
                        <span>{t("ui.quoted.price")}</span>
                        <strong>{formatSharedOfferPrice(sharedOffer)}</strong>
                      </div>
                      <div>
                        <span>{t("ui.quantity")}</span>
                        <strong>
                          {formatSharedOfferQuantity(
                            sharedOffer,
                            inquiry.quantity,
                          )}
                        </strong>
                      </div>
                      <div>
                        <span>{t("ui.lead.time")}</span>
                        <strong>
                          {sharedOffer.leadTime || EMPTY_DISPLAY_VALUE}
                        </strong>
                      </div>
                      <div className="reference-wide">
                <span>{t("ui.notes")}</span>
                <strong>{sharedOffer.notes || "—"}</strong>
              </div>
                    </div>
                  </div>
                </article>
                {sharedOffers.slice(1).map((offer, index) => {
              const details = [
                {
                  key: "price",
                  label: t("ui.quoted.price"),
                  value: formatSharedOfferPrice(offer),
                },
                {
                  key: "quantity",
                  label: t("ui.quantity"),
                  value: formatSharedOfferQuantity(offer, inquiry.quantity),
                },
                offer.leadTime && {
                  key: "lead-time",
                  label: t("ui.lead.time"),
                  value: offer.leadTime,
                },
                offer.notes && {
                  key: "notes",
                  label: t("ui.notes"),
                  value: offer.notes,
                },
                ...(offer.additionalFields || []).map((field, fieldIndex) => ({
                  key: `additional-${fieldIndex}-${field.label}`,
                  label: text(field.label),
                  value: field.value,
                })),
              ].filter(
                (field) =>
                  field &&
                  field.value !== null &&
                  field.value !== undefined &&
                  field.value !== "",
              );
              const historyCardId = `reference-offer-history-${String(
                offer.quotationId || offer.reference || offer.submittedAt || index,
              ).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
              const isHistoryDetailsOpen =
                historyOfferDetailsOpen[historyCardId] === true;
              const historyVendorName = offer.vendorName || offerVendorName;
              const historyVendorInitial =
                historyVendorName.trim().charAt(0).toUpperCase() || "V";
              return (
                <article
                  className="reference-offer-history-entry"
                  key={`${offer.quotationId || offer.reference || offer.submittedAt || "quotation"}-${index + 1}`}
                >
                  <div className="reference-offer-history-heading">
                    <div className="reference-offer-vendor">
                      <span className="reference-offer-vendor-avatar">
                        {historyVendorInitial}
                      </span>
                      <div className="reference-offer-history-identity">
                        <strong>{historyVendorName}</strong>
                        <small>
                          {offer.quotationId ||
                            offer.reference ||
                            EMPTY_DISPLAY_VALUE}
                        </small>
                      </div>
                    </div>
                    <em
                      className={`reference-status reference-offer-vendor-status ${offer.status.toLowerCase()}`}
                    >
                      {translateStatus(offer.status)}
                    </em>
                    <button
                      type="button"
                      className="reference-offer-toggle"
                      aria-expanded={isHistoryDetailsOpen}
                      aria-controls={`${historyCardId}-details`}
                      aria-label={t("ui.viewDetails")}
                      onClick={() =>
                        setHistoryOfferDetailsOpen((expanded) => ({
                          ...expanded,
                          [historyCardId]: !isHistoryDetailsOpen,
                        }))
                      }
                    >
                      <span>{t("ui.viewDetails")}</span>
                      <svg
                        className="reference-offer-chevron"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          d="m6 9 6 6 6-6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <div
                    id={`${historyCardId}-details`}
                    className="reference-offer-history-details"
                    hidden={!isHistoryDetailsOpen}
                  >
                    <div className="reference-offer-history-meta">
                      {details.map((field) => (
                        <div
                          className="reference-offer-history-field"
                          key={field.key}
                        >
                          <span>{field.label}</span>
                          <strong>{field.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                </article>
              );
            })}
              </>
            ) : (
              <p className="reference-empty">{t("ui.noQuotationSubmitted")}</p>
            )}
          </section>
        )}

        <section className="reference-card reference-attributes">
          <div className="reference-section-heading">
            <div>
              <h2>{t("ui.product.attributes")}</h2>
              <p>{t("ui.key.requirements.submitted.with.this.inquiry")}</p>
            </div>
            {/* <span className="reference-attribute-count">{t("ui.4.fields")}</span> */}
          </div>
          <div className="reference-attributes-grid">
            <label>{t("ui.sector")}<input value={inquiry.sector} readOnly />
            </label>
            <label>{t("ui.product")}<input value={inquiry.commodity} readOnly />
            </label>
            <label>{t("ui.quantity.hpfikr")}<input value={inquiry.quantity} readOnly />
            </label>
            <label>{t("ui.date")}<input value={inquiry.inquiryDate} readOnly />
            </label>
            <label className="reference-wide">{t("ui.destination.port.location")}
              <textarea value={inquiry.destinationPortLocation} readOnly />
            </label>
          </div>
        </section>
      </div>
      <section className="inquiry-modern-header">
        <div className="inquiry-modern-header-content">
          <span className="inquiry-modern-label">{t("ui.inquiry.details")}</span>

          <div className="inquiry-modern-title">
            <h1>{inquiry.title}</h1>

            <span className={`inquiry-detail-status ${inquiry.statusClass}`}>
              {translateStatus(inquiry.status)}
            </span>
          </div>

          <p>{t("ui.review.product.requirements.commercial.information.client.details.and.shipment.progress")}</p>

          <div className="inquiry-modern-meta">
            <span>{inquiry.id}</span>

            <i />

            <span>{t("ui.created")}{" "}{inquiry.inquiryDate}</span>

            <i />

            <span>{t("ui.updated")}{" "}{inquiry.lastUpdate}</span>
          </div>
        </div>
      </section>
      <section className="inquiry-modern-summary">
        <div className="inquiry-modern-summary-card">
          <div className="inquiry-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 5H19V19H5V5Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />

              <path
                d="M8 9H16M8 13H13"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <div>
            <span>{t("ui.inquiry.id")}</span>

            <strong>{inquiry.id}</strong>
          </div>
        </div>

        <div className="inquiry-modern-summary-card">
          <div className="inquiry-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 7L12 3L19 7V17L12 21L5 17V7Z"
                stroke="currentColor"
                strokeWidth="1.6"
              />

              <path
                d="M5 7L12 11L19 7"
                stroke="currentColor"
                strokeWidth="1.6"
              />
            </svg>
          </div>

          <div>
            <span>{t("ui.product.meu6z3")}</span>

            <strong>{inquiry.commodity}</strong>
          </div>
        </div>

        <div className="inquiry-modern-summary-card">
          <div className="inquiry-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M6 4V7M18 4V7M4 9H20M5 6H19V20H5V6Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>

          <div>
            <span>{t("ui.submitted")}</span>

            <strong>{inquiry.inquiryDate}</strong>
          </div>
        </div>

        <div className="inquiry-modern-summary-card">
          <div className="inquiry-modern-summary-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <circle
                cx="12"
                cy="12"
                r="8"
                stroke="currentColor"
                strokeWidth="1.6"
              />

              <path
                d="M8.5 12L11 14.5L16 9.5"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div>
            <span>{t("ui.current.status")}</span>

            <strong>{translateStatus(inquiry.statusText)}</strong>
          </div>
        </div>
      </section>
      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          <div>
            <h2>{t("ui.product.details")}</h2>

            <p>{t("ui.product.specification.and.quantity.requested.by.the.buyer")}</p>
          </div>
          <div className="inquiry-action-buttons">
            <button
              type="button"
              className="inquiry-action-secondary"
              onClick={openInquiryChat}
            >
              <svg
                className="inquiry-button-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="M5 6.5A2.5 2.5 0 0 1 7.5 4h9A2.5 2.5 0 0 1 19 6.5v6a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3v-3.18A2.5 2.5 0 0 1 5 12.5v-6Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
              </svg>{t("ui.message.16953uv")}</button>
            <button
              type="button"
              className="inquiry-action-primary"
              onClick={() => setSharePriceOpen(true)}
            >
              <svg
                className="inquiry-button-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  d="m13.2 4.8 6 6-8.4 8.4H4.8v-6l8.4-8.4Z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.7"
                  strokeLinejoin="round"
                />
                <circle cx="9" cy="14" r="1.2" fill="currentColor" />
              </svg>{t("ui.share.a.price.19vgsvd")}</button>
          </div>
        </div>

        <div className="inquiry-modern-product">
          <div className="inquiry-modern-product-image">
            <img
              src={inquiry.image}
              alt={inquiry.title}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          </div>

          <div className="inquiry-modern-product-content">
            <span>{t("ui.commodity")}</span>

            <h3>{inquiry.commodity}</h3>

            <p>{inquiry.grade}</p>
          </div>
        </div>

        <div className="inquiry-modern-field-grid">
          <div className="inquiry-modern-field">
            <span>{t("ui.quantity")}</span>

            <strong>{inquiry.quantity}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.tolerance")}</span>

            <strong>{inquiry.tolerance}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.target.price")}</span>

            <strong>{inquiry.targetPrice}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.buyer.reference")}</span>

            <strong>{inquiry.buyerReference}</strong>
          </div>
        </div>
      </section>

      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          {sharedOffer && (
            <span
              className={`shared-offer-status ${sharedOffer.status.toLowerCase()}`}
            >
              {translateStatus(sharedOffer.status)}
            </span>
          )}
          <div>
            <h2>{t("ui.shared.image")}</h2>

            <p>
              {sharedOffer
                ? t("ui.offerDetailsShared")
                : t("ui.imageShared")}
            </p>
          </div>
        </div>
        <div className="inquiry-shared-image">
          <img src={inquiry.image} alt={inquiry.title} />
        </div>
        {sharedOffer && (
          <div className="shared-offer-details">
            <div>
              <span>{t("ui.quoted.price")}</span>
              <strong>{formatSharedOfferPrice(sharedOffer)}</strong>
            </div>
            <div>
              <span>{t("ui.quantity")}</span>
              <strong>
                {formatSharedOfferQuantity(sharedOffer, inquiry.quantity)}
              </strong>
            </div>
            <div>
              <span>{t("ui.lead.time")}</span>
              <strong>{sharedOffer.leadTime || EMPTY_DISPLAY_VALUE}</strong>
            </div>
            <div className="shared-offer-wide">
              <span>{t("ui.message.17bmnl3")}</span>
              <strong>{sharedOffer.message || EMPTY_DISPLAY_VALUE}</strong>
            </div>
            {sharedOffer.submittedAt && (
              <div>
                <span>{t("ui.quoted.on")}</span>
                <strong>{sharedOffer.submittedAt}</strong>
              </div>
            )}
            {(sharedOffer.additionalFields || []).map((field) => (
              <div key={field.label}>
                <span>{text(field.label)}</span>
                <strong>{field.value}</strong>
              </div>
            ))}
            <div className="shared-offer-wide">
              <span>{t("ui.notes")}</span>
              <strong>{sharedOffer.notes || "—"}</strong>
            </div>
          </div>
        )}
        {sharedOffers.slice(1).map((offer, index) => (
          <div
            className={
              "shared-offer-history-entry" +
              (offer.status.toLowerCase() === "rejected" ? " is-rejected" : "")
            }
            key={`${offer.quotationId || offer.reference || offer.submittedAt || "quotation"}-${index + 1}`}
          >
            <div className="shared-offer-history-heading">
              <div className="shared-offer-history-identity">
                <small>
                  {offer.quotationId ||
                    offer.reference ||
                    EMPTY_DISPLAY_VALUE}
                </small>
                <strong>{formatSharedOfferPrice(offer)}</strong>
              </div>
              <span
                className={`shared-offer-status ${offer.status.toLowerCase()}`}
              >
                {translateStatus(offer.status)}
              </span>
            </div>
            <div className="shared-offer-details">
              <div>
                <span>{t("ui.quantity")}</span>
                <strong>
                  {formatSharedOfferQuantity(offer, inquiry.quantity)}
                </strong>
              </div>
              <div>
                <span>{t("ui.lead.time")}</span>
                <strong>{offer.leadTime || EMPTY_DISPLAY_VALUE}</strong>
              </div>
              {offer.submittedAt && (
                <div>
                  <span>{t("ui.quoted.on")}</span>
                  <strong>{offer.submittedAt}</strong>
                </div>
              )}
              <div className="shared-offer-wide">
                <span>{t("ui.message.17bmnl3")}</span>
                <strong>{offer.message || EMPTY_DISPLAY_VALUE}</strong>
              </div>
              <div className="shared-offer-wide">
                <span>{t("ui.notes")}</span>
                <strong>{offer.notes || EMPTY_DISPLAY_VALUE}</strong>
              </div>
              {(offer.additionalFields || []).map((field) => (
                <div key={field.label}>
                  <span>{text(field.label)}</span>
                  <strong>{field.value}</strong>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          <div>
            <h2>{t("ui.product.details")}</h2>
            <p>{t("ui.requested.product.information.from.the.buyer")}</p>
          </div>
        </div>
        <div className="inquiry-modern-field-grid inquiry-modern-card-fields">
          <div className="inquiry-modern-field">
            <span>{t("ui.sector.14w3j46")}</span>
            <strong>{inquiry.sector}</strong>
          </div>
          <div className="inquiry-modern-field">
            <span>{t("ui.product.meu6z3")}</span>
            <strong>{inquiry.commodity}</strong>
          </div>
          <div className="inquiry-modern-field">
            <span>{t("ui.quantity")}</span>
            <strong>{inquiry.quantity}</strong>
          </div>
          <div className="inquiry-modern-field">
            <span>{t("ui.date.19hvy")}</span>
            <strong>{inquiry.inquiryDate}</strong>
          </div>
          <div className="inquiry-modern-field inquiry-detail-description">
            <span>{t("ui.description")}</span>
            <strong>{inquiry.message}</strong>
          </div>
        </div>
      </section>

      {/* Existing commercial information */}
      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          <div>
            <h2>{t("ui.commercial.delivery")}</h2>
            <p>{t("ui.trade.conditions.payment.terms.and.destination.information")}</p>
          </div>
        </div>
        <div className="inquiry-modern-field-grid inquiry-modern-card-fields">
          <div className="inquiry-modern-field">
            <span>{t("ui.delivery.location")}</span>

            <strong>{inquiry.deliveryLocation}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.delivery.terms")}</span>

            <strong>{inquiry.deliveryTerm}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.payment.terms")}</span>

            <strong>{inquiry.paymentTerm}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.shipment.method")}</span>

            <strong>{inquiry.preferredShipment}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.port.of.loading")}</span>

            <strong>{inquiry.portOfLoading}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.port.of.discharge")}</span>

            <strong>{inquiry.portOfDischarge}</strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.country")}</span>

            <strong>
              {inquiry.flag} {inquiry.country}
            </strong>
          </div>

          <div className="inquiry-modern-field">
            <span>{t("ui.inquiry.date")}</span>

            <strong>{inquiry.inquiryDate}</strong>
          </div>
        </div>
      </section>

      {sharePriceOpen && (
        <div
          className="share-price-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-price-title"
        >
          <div className="share-price-modal">
            <button
              type="button"
              className="share-price-close"
              aria-label={t("ui.close")}
              onClick={() => setSharePriceOpen(false)}
            >
              ×
            </button>
            <h2 id="share-price-title">{t("ui.share.a.price.tsi0yx")}</h2>
            <p className="share-price-description">{t("ui.send.your.best.offer.for.this.inquiry.to.the.buyer")}</p>
            {/* <div className="share-price-product">
              <strong>{inquiry.commodity}</strong>
              <span>{inquiry.message}</span>
            </div> */}
            {quotationError && (
              <p className="share-price-error" role="alert">
                {quotationError}
              </p>
            )}
            <form className="share-price-form" onSubmit={submitPrice}>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="share-price-field">
                    <span className="share-price-field-label">{t("ui.quoted.price.x6e8i9")}</span>
                    <div className="share-price-input-group">
                      <div className="share-price-currency-wrap">
                        <CustomDropdown
                          className="share-price-currency-dropdown"
                          ariaLabel={t("ui.selectPriceCurrency")}
                          value={priceForm.currencyCode}
                          options={SHARE_PRICE_CURRENCY_OPTIONS}
                          placeholder={t("ui.currency")}
                          searchable
                          searchPlaceholder={t("ui.searchCountriesCurrencies")}
                          emptyMessage={t("ui.noCurrencies")}
                          onChange={(currencyCode) =>
                            updatePriceForm("currencyCode", currencyCode)
                          }
                        />
                      </div>
                      <div className="share-price-amount">
                        <span
                          className="share-price-currency-symbol"
                          aria-hidden="true"
                        >
                          {priceCurrencySymbol}
                        </span>
                        <input
                          type="number"
                          onKeyDown={handleNumericInputKeyDown}
                          aria-label={t("ui.quoted.price")}
                          value={priceForm.quotedPrice}
                          required
                          min="0"
                          step="any"
                          onChange={(event) =>
                            updatePriceForm("quotedPrice", sanitizeNumericInput(event.target.value))
                          }
                          placeholder={t("ui.enter.price")}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                  <div className="col-md-6">
                  <label>{t("ui.lead.time.i357m9")}<input
                      value={priceForm.leadTime}
                      onChange={(event) =>
                        updatePriceForm("leadTime", event.target.value)
                      }
                      placeholder={t("ui.e.g.14.days")}
                    />
                  </label>
                </div>
                <div className="col-md-6">
                  <div className="share-price-field">
                    <span className="share-price-field-label">{t("ui.quantity")}</span>
                    <div className="share-price-quantity-control">
                      <input
                        className="share-price-quantity-input"
                        type="number"
                        onKeyDown={handleNumericInputKeyDown}
                        aria-label={t("ui.quantity.amount")}
                        required
                        min="0"
                        step="any"
                        value={priceForm.quantity}
                        onChange={(event) =>
                          updatePriceForm("quantity", sanitizeNumericInput(event.target.value))
                        }
                        placeholder={t("ui.amount")}
                      />
                      <div className="share-price-unit-wrap">
                        <CustomDropdown
                          className="share-price-unit-dropdown"
                          ariaLabel={t("ui.selectQuantityUnit")}
                          value={priceForm.unit}
                          options={QUANTITY_UNIT_OPTIONS}
                          placeholder={t("ui.unit")}
                          searchable
                          searchPlaceholder={t("ui.searchUnits")}
                          emptyMessage={t("ui.noUnits")}
                          onChange={(unit) => updatePriceForm("unit", unit)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-12">
                  <label>{t("ui.description.1vdjb24")}<textarea
                      value={priceForm.description}
                      onChange={(event) =>
                        updatePriceForm("description", event.target.value)
                      }
                      placeholder={t("ui.describe.your.offer")}
                    />
                  </label>
                </div>
                <div className="col-12">
                  <label>{t("ui.notes.offer.details.1glnudq")}<textarea
                      value={priceForm.notes}
                      onChange={(event) =>
                        updatePriceForm("notes", event.target.value)
                      }
                      placeholder={t("ui.add.notes.or.additional.terms")}
                    />
                  </label>
                </div>
                <div className="col-12">
                  <div className="share-price-actions">
                    <button
                      type="button"
                      className="share-price-cancel"
                      onClick={() => setSharePriceOpen(false)}
                    >{t("ui.cancel")}</button>
                    <button
                      type="submit"
                      className="share-price-submit"
                      disabled={!canSubmitPrice || quotationLoading}
                    >
                      {quotationLoading ? t("ui.submitting") : t("ui.sharePrice")}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          <div>
            <h2>{t("ui.client.information")}</h2>

            <p>{t("ui.contact.details.for.the.client.associated.with.this.inquiry")}</p>
          </div>
        </div>

        <div className="inquiry-modern-seller">
          <div className="inquiry-modern-seller-profile">
            <div className="inquiry-modern-seller-avatar">
              {inquiry.seller
                .split(" ")
                .map((item) => item.charAt(0))
                .slice(0, 2)
                .join("")}
            </div>

            <div>
              <h3>{inquiry.seller}</h3>

              <p>
                {inquiry.flag} {inquiry.country}
              </p>

              <span>
                {inquiry.clientVerified
                  ? t("ui.verifiedClient")
                  : t("ui.notVerified")}
              </span>
            </div>
          </div>

          <div className="inquiry-modern-seller-detail">
            <span>{t("ui.email.address.1mz83lc")}</span>

            <strong>{inquiry.sellerEmail}</strong>
          </div>

          <div className="inquiry-modern-seller-detail">
            <span>{t("ui.phone.number")}</span>

            <strong>{inquiry.sellerPhone}</strong>
          </div>
        </div>
      </section>

      {shipment && (
        <section className="inquiry-modern-card shipment-modern-card">
          <div className="inquiry-modern-card-heading shipment-modern-heading">
            <div>
              <h2>{t("ui.shipment")}</h2>

              <p>{t("ui.shipment.details.and.delivery.progress")}</p>
            </div>

            <div className="shipment-update-dropdown" ref={dropdownRef}>
              <button
                type="button"
                className="shipment-update-trigger"
                onClick={() => setShipmentDropdownOpen((prev) => !prev)}
              >{t("ui.update.shipment")}<svg
                  viewBox="0 0 24 24"
                  fill="none"
                  className={shipmentDropdownOpen ? "active" : ""}
                >
                  <path
                    d="M7 10L12 15L17 10"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {shipmentDropdownOpen && (
                <div className="shipment-update-menu">
                  <div className="shipment-update-menu-title">{t("ui.update.shipment.status")}</div>

                  {shipment.stages.map((stage, index) => (
                    <button
                      type="button"
                      key={stage.title}
                      className={`shipment-update-option ${
                        stage.current ? "active" : ""
                      }`}
                      onClick={() => handleShipmentStageUpdate(index)}
                    >
                      <span className="shipment-update-option-icon">
                        {stage.current ? "✓" : index + 1}
                      </span>

                      <span className="shipment-update-option-content">
                        <strong>{stage.title}</strong>

                        <small>
                          {stage.current
                            ? t("ui.currentStatus")
                            : stage.completed
                              ? t("ui.completed")
                              : t("ui.markAsCurrent")}
                        </small>
                      </span>

                      {stage.current && (
                        <span className="shipment-update-current">{t("ui.current")}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="shipment-progress-section">
            {/* <div className="shipment-progress-section-heading">
              <h3>Shipment progress</h3>

              <p>
                Track each stage of the shipment from order confirmation through
                final delivery.
              </p>
            </div> */}

            <div className="shipment-progress-vertical">
              {shipment.stages.map((stage, index) => {
                const isLast = index === shipment.stages.length - 1;

                return (
                  <div
                    key={stage.title}
                    className={`shipment-progress-step ${
                      stage.completed ? "completed" : ""
                    } ${stage.current ? "current" : ""}`}
                  >
                    {/* LEFT */}

                    <div className="shipment-progress-step-left">
                      <div className="shipment-progress-step-circle">
                        {stage.completed && !stage.current ? (
                          <svg viewBox="0 0 24 24" fill="none">
                            <path
                              d="M7 12.5L10.5 16L17 9"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : stage.current ? (
                          <span />
                        ) : (
                          index + 1
                        )}
                      </div>

                      {!isLast && (
                        <div className="shipment-progress-step-line" />
                      )}
                    </div>

                    {/* CONTENT */}

                    <div className="shipment-progress-step-content">
                      <div className="shipment-progress-step-top">
                        <div>
                          <h4>{sentenceCaseLabel(stage.title)}</h4>

                          {stage.current && (
                            <span className="shipment-progress-current-badge">{t("ui.current.193jzg9")}</span>
                          )}
                        </div>

                        <span className="shipment-progress-step-date">
                          {stage.date}
                        </span>
                      </div>

                      <p>{stage.description}</p>

                      {stage.current && (
                        <div className="shipment-progress-current-note">
                          <svg viewBox="0 0 24 24" fill="none">
                            <circle
                              cx="12"
                              cy="12"
                              r="8"
                              stroke="currentColor"
                              strokeWidth="1.7"
                            />

                            <path
                              d="M12 8V12.5"
                              stroke="currentColor"
                              strokeWidth="1.7"
                              strokeLinecap="round"
                            />

                            <circle
                              cx="12"
                              cy="16"
                              r="0.8"
                              fill="currentColor"
                            />
                          </svg>{t("ui.shipment.is.currently.at.this.stage")}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}
      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          <div>
            <h2>{t("ui.inquiry.message")}</h2>

            <p>{t("ui.original.requirements.shared.with.this.inquiry")}</p>
          </div>
        </div>

        <div className="inquiry-modern-message">
          <div className="inquiry-modern-message-icon">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M5 5H19V16H9L5 20V5Z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <p>{inquiry.message}</p>
        </div>
      </section>
      <section className="inquiry-modern-card">
        <div className="inquiry-modern-card-heading">
          <div>
            <h2>{t("ui.inquiry.activity")}</h2>

            <p>{t("ui.recent.updates.and.activity.related.to.this.inquiry")}</p>
          </div>
        </div>

        <div className="inquiry-modern-activity">
          {inquiry.timeline.map((item, index) => (
            <div className="inquiry-modern-activity-item" key={index}>
              <div className="inquiry-modern-activity-side">
                <span
                  className={`inquiry-modern-activity-dot ${item.status}`}
                />

                {index < inquiry.timeline.length - 1 && (
                  <span className="inquiry-modern-activity-line" />
                )}
              </div>

              <div className="inquiry-modern-activity-content">
                <strong>{item.title}</strong>

                <p>{item.description}</p>

                <small>{item.time}</small>
              </div>
            </div>
          ))}
        </div>
      </section>
      {createQuimeraOrderOpen && (
        <CreateQuimeraOrderModal
          inquiry={inquiry}
          onClose={() => setCreateQuimeraOrderOpen(false)}
          onCreate={handleCreateQuimeraOrder}
        />
      )}
    </div>
  );
}
